/** Unified upload: .zip report package, .xbrl/.xml, or multiple CSV/JSON files. */

export const FILING_UPLOAD_ACCEPT =
  '.zip,.csv,.json,.xbrl,.xml,.ZIP,.CSV,.JSON,.XBRL,.XML';

export const FILING_UPLOAD_LABEL = 'Report package or filing';

export const FILING_UPLOAD_HINT =
  'One file: .zip (xBRL-CSV package) or .xbrl / .xml (legacy). Or select all CSV/JSON files from a package together.';

export type FilingUploadKind = 'xml' | 'zip' | 'csv-package';

export type FilingUploadPayload = {
  displayName: string;
  kind: FilingUploadKind;
  /** Primary bytes for xml/zip single-file upload. */
  fileBytes?: ArrayBuffer;
  /** All files for csv-package (includes .json entry point when present). */
  packageFiles?: Array<{ name: string; fileBytes: ArrayBuffer }>;
};

function ext(name: string): string {
  const i = name.lastIndexOf('.');
  return i >= 0 ? name.slice(i + 1).toLowerCase() : '';
}

export function describeFilingUpload(payload: FilingUploadPayload): string {
  switch (payload.kind) {
    case 'zip':
      return `xBRL-CSV package (zip): ${payload.displayName}`;
    case 'xml':
      return `xBRL-XML instance: ${payload.displayName}`;
    case 'csv-package':
      return `xBRL-CSV files (${payload.packageFiles?.length ?? 0}): ${payload.displayName}`;
  }
}

export async function buildFilingUpload(files: File[]): Promise<FilingUploadPayload> {
  if (!files.length) {
    throw new Error('No files selected');
  }

  if (files.length === 1) {
    const file = files[0];
    const extension = ext(file.name);
    const buf = await file.arrayBuffer();

    if (extension === 'zip') {
      return { displayName: file.name, kind: 'zip', fileBytes: buf };
    }
    if (extension === 'xbrl' || extension === 'xml') {
      return { displayName: file.name, kind: 'xml', fileBytes: buf };
    }
    if (extension === 'csv') {
      throw new Error(
        'A single CSV is not enough — upload the full .zip report package, or select all CSV and JSON files from the package together.',
      );
    }
    throw new Error(`Unsupported file type: .${extension || 'unknown'}`);
  }

  const packageFiles: FilingUploadPayload['packageFiles'] = [];
  for (const file of files) {
    const extension = ext(file.name);
    if (!['csv', 'json'].includes(extension)) {
      throw new Error(
        `Unsupported file in multi-select: ${file.name}. Use .zip, one .xbrl/.xml, or only .csv/.json files.`,
      );
    }
    packageFiles.push({ name: file.name, fileBytes: await file.arrayBuffer() });
  }

  const baseName = packageFiles[0]?.name.replace(/\.[^.]+$/, '') ?? 'report-package';
  return {
    displayName: `${baseName}-package (${packageFiles.length} files)`,
    kind: 'csv-package',
    packageFiles,
  };
}
